// The contents' filter bar (the Mac's FilterBar; `design-docs/Binder filters — design.md`, restyled by
// `design-docs/Sidebar redesign — design.md`): words typed filter as they're typed and offer facets that match them
// (choosing one makes it a chip); the filter button adds a facet — quick filters, collections, then every facet. Chips,
// or a collection loaded, show in a card: the collection's colour and icon (or "Filter", unsaved), how much matches, the
// rules as chips joined by "and" / "or", Update and Revert when the chips no longer say what the collection says. A chip
// opens its editor under the card. A breadcrumb above the field shows the folder the filter keeps to (Focus). The
// matches can be shown as a list in reading order, with the words found under each title; the contents' foot says how
// many documents the filter leaves out, hidden or drawn faded.
import { useMemo, useRef, useState } from 'react';
import { makeID, type EntityKind, type OpenedProject, type TreeNode } from '@writing-suite/core';
import { beatSheetOf, collectionIcon, collectionInk, isUnsupported, newRule, savedSearchesOf, type SavedSearch, type SavedSearchRule } from '@writing-suite/core/plan';
import { readCommentCategories } from '@writing-suite/core/revise';
import {
  arrangementNoun, countLine, facetLabel, facetParts, facetStyle, filterAsCollection, filterBy, filterCaption, filterChoices, filteredCounts, filterSuggestions, focusNodeId,
  isFocusable, isUnfilled, leftOutLine, orderedMatches, quickFilters, scopeFor, TYPED_RULE_ID, type DocumentNoun, type FilterSuggestion, type Snippet,
} from '../store/binderFilter.ts';
import { planEdit, PREF, useSharedPreference } from '../store/plan.ts';
import { projectTags } from '../store/planModel.ts';
import { announce } from '../shell/context.ts';
import { useWorkspace, type BinderFilter, type Workspace } from '../shell/workspace.ts';
import { cssColor, pathTo } from './helpers.ts';
import { Icon, Tile, type Ink } from './Icons.tsx';
import { ConfirmDialog, Dot, MenuButton, MenuPopup, type MenuEntry } from './Menu.tsx';
import { FacetEditor } from './FacetEditor.tsx';
import { CollectionEditor } from './plan/CollectionEditor.tsx';

/** The rule whose editor is open, and (for a person, place or thing not chosen yet) which kind it offers. */
interface Editing {
  id: string;
  kind?: EntityKind | 'object-or-motif';
}

/** The collection dialog's subject: a collection to edit, or a new one (from the filter, or from nothing). */
export interface CollectionDialogState {
  search: SavedSearch | null;
  draft?: Partial<SavedSearch>;
}

export function FilterBar() {
  const ws = useWorkspace();
  const filter = ws?.filter ?? null;
  const [editing, setEditingState] = useState<Editing | null>(null);
  const [typing, setTyping] = useState(false);
  const [dialog, setDialog] = useState<CollectionDialogState | null>(null);
  const [deleting, setDeleting] = useState<SavedSearch | null>(null);
  const field = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const project = ws?.project as OpenedProject | undefined;
  const tags = useMemo(() => (project?.record ? projectTags(project) : []), [project]);
  if (!ws || !filter || !project?.record) return null;
  const { state } = filter;
  const chips = state.filter?.rules ?? [];
  const nodeTitle = (id: string) => (ws.fullTree ? pathTo(ws.fullTree, id)?.at(-1)?.title || 'Untitled' : null);
  const label = (rule: SavedSearchRule) => facetLabel(rule, project, nodeTitle);

  // Closing an editor takes away a facet that was never filled in.
  const setEditing = (next: Editing | null) => {
    if (editing && editing.id !== next?.id) {
      const rule = chips.find((r) => r.id === editing.id);
      if (rule && isUnfilled(rule)) filter.removeFacet(rule.id);
    }
    setEditingState(next);
  };
  const editingRule = editing ? chips.find((r) => r.id === editing.id) ?? null : null;

  const suggestions = typing && state.text.trim() ? filterSuggestions(project, tags, state.text) : [];
  const choose = (s: FilterSuggestion) => {
    filter.set(filterBy({ ...state, text: '' }, s.rules, true, !!filter.saved));
    announce(`Filtering by ${s.facet} ${s.value}`);
    field.current?.focus();
  };

  const focusId = focusNodeId(state);
  const focusPath = focusId && ws.fullTree ? pathTo(ws.fullTree, focusId) : null;
  const noun = arrangementNoun(ws.project.type, ws.arrangement.role);
  const writable = ws.canWrite && !!ws.live;
  // Save as Collection… and Save as New Collection…: the dialog, filled from the filter.
  const saveAs = (fresh: boolean) => {
    const made = filterAsCollection(state, fresh ? '' : state.text.trim(), filter.saved?.scope ?? scopeFor(ws.arrangement.role));
    if (!made) return;
    setDialog({ search: null, draft: { ...made, ...(filter.saved && fresh ? { matchAll: filter.saved.matchAll } : {}) } });
  };
  // Update: the chips into the collection, its name, colour, icon and scope kept.
  const update = () => {
    const saved = filter.saved;
    if (!saved || !ws.live) return;
    const made = filterAsCollection(state, saved.name, saved.scope);
    const search: SavedSearch = { ...saved, rules: made?.rules ?? [] };
    planEdit(ws.live, (e) => e.putSavedSearch(search));
    filter.load(search);
    announce(`Collection “${search.name}” updated`);
  };
  const showCard = chips.length > 0 || !!filter.saved;

  return (
    <div className="filter-bar" role="search" aria-label="Filter the shelf">
      {focusPath ? (
        <nav className="filter-focus" aria-label="Focus">
          <button type="button" className="link-button" onClick={() => filter.focus(null)} aria-label={`Show all of ${ws.arrangement.name}`}>
            {ws.arrangement.name}
          </button>
          {focusPath.map((n) => (
            <span key={n.id} className="filter-focus-step">
              <span aria-hidden="true">›</span>
              <button type="button" className="link-button" disabled={n.id === focusId} aria-current={n.id === focusId ? 'location' : undefined} onClick={() => filter.focus(n.id)}>
                {n.title || 'Untitled'}
              </button>
            </span>
          ))}
        </nav>
      ) : null}
      <div className="filter-search">
        <div className="filter-field">
          <Icon name="search" size={15} className="filter-field-icon" />
          <input
            ref={field}
            type="search"
            className="filter-input"
            placeholder={noun === 'document' ? 'Search' : `Search ${noun}s`}
            aria-label="Search or filter the shelf"
            aria-controls={suggestions.length ? 'filter-suggestions' : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
            value={state.text}
            onChange={(e) => filter.set({ ...state, text: e.target.value })}
            onFocus={() => setTyping(true)}
            onBlur={(e) => {
              if (!suggestionsRef.current?.contains(e.relatedTarget as Node)) setTyping(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && (filter.active || state.text)) {
                e.preventDefault();
                e.stopPropagation();
                filter.clear();
              } else if (e.key === 'ArrowDown' && suggestions.length) {
                e.preventDefault();
                suggestionsRef.current?.querySelector<HTMLElement>('button')?.focus();
              } else if (e.key === 'Enter') {
                // The words stay a text facet, as a search.
                setTyping(false);
              }
            }}
          />
          <MenuButton label="Add a filter" className="filter-add" title="Add a filter" menuLabel="Add a filter" entries={() => addEntries(ws, filter, project, tags, (e) => setEditing(e))}>
            <Icon name="filter" size={15} />
          </MenuButton>
        </div>
        {suggestions.length ? (
          <ul className="filter-suggestions" id="filter-suggestions" ref={suggestionsRef} aria-label="Filter by">
            {suggestions.map((s, i) => (
              <li key={`${s.facet}\u001f${s.value}`}>
                <button
                  type="button"
                  onClick={() => choose(s)}
                  onBlur={(e) => {
                    if (!suggestionsRef.current?.contains(e.relatedTarget as Node) && e.relatedTarget !== field.current) setTyping(false);
                  }}
                  onKeyDown={(e) => {
                    const buttons = [...(suggestionsRef.current?.querySelectorAll<HTMLElement>('button') ?? [])];
                    if (e.key === 'ArrowDown') { e.preventDefault(); buttons[i + 1]?.focus(); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); (buttons[i - 1] ?? field.current)?.focus(); }
                    else if (e.key === 'Escape') { e.preventDefault(); field.current?.focus(); setTyping(false); }
                  }}
                  aria-label={`Filter by ${s.facet} ${s.value}`}
                >
                  <span className="filter-suggestion-facet">{s.facet}</span>
                  <span className="filter-suggestion-value">{s.value}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {showCard ? (
        <FilterCard
          ws={ws}
          filter={filter}
          project={project}
          noun={noun}
          writable={writable}
          editing={editing?.id ?? null}
          label={label}
          parts={(rule) => facetParts(rule, project, nodeTitle)}
          onChip={(id) => setEditing(editing?.id === id ? null : { id })}
          onRemoveChip={(id) => {
            if (editing?.id === id) setEditingState(null);
            filter.removeFacet(id);
          }}
          onEdit={() => (filter.saved ? setDialog({ search: filter.saved }) : saveAs(false))}
          onSaveAsNew={() => saveAs(true)}
          onUpdate={update}
          onDelete={() => filter.saved && setDeleting(filter.saved)}
        />
      ) : null}
      {editingRule ? (
        <FacetEditor
          key={editingRule.id}
          rule={editingRule}
          entityKind={editing?.kind}
          project={project}
          tags={tags}
          label={label(editingRule)}
          onChange={(r) => filter.replaceFacet(r)}
          onRemove={() => {
            setEditingState(null);
            filter.removeFacet(editingRule.id);
          }}
          onDone={() => setEditing(null)}
        />
      ) : null}
      {!showCard && filter.view ? (
        // The words typed alone: the slim count line.
        <div className="filter-count">
          <div className="filter-count-text">
            <span className="filter-count-line" role="status" data-documents={filter.view.result.documents} data-total={filter.view.result.total}>
              {countLine(filter.view.result, noun)}
            </span>
          </div>
          <MenuButton
            label="Filter options"
            className="quiet-button filter-options"
            menuLabel="Filter"
            entries={() => [
              { kind: 'radio', label: 'As the Shelf', checked: !state.list, onSelect: () => filter.set({ ...state, list: false }) },
              { kind: 'radio', label: 'As a List', checked: state.list, onSelect: () => filter.set({ ...state, list: true }) },
              { kind: 'separator' },
              { label: 'Save as Collection…', disabled: !writable || !filter.active, onSelect: () => saveAs(false) },
              { kind: 'separator' },
              { label: 'Clear Filter', onSelect: () => filter.clear() },
            ] satisfies MenuEntry[]}
          >
            ⋯
          </MenuButton>
          <button type="button" className="link-button filter-clear" onClick={() => filter.clear()}>
            Clear
          </button>
        </div>
      ) : null}
      {dialog ? (
        <CollectionEditor
          project={project}
          search={dialog.search}
          draft={dialog.draft}
          onCancel={() => setDialog(null)}
          onSave={(s) => {
            setDialog(null);
            if (!ws.live) return;
            planEdit(ws.live, (e) => e.putSavedSearch(s));
            filter.load(s);
            announce(`Collection “${s.name}” saved`);
          }}
        />
      ) : null}
      {deleting ? <DeleteCollectionDialog ws={ws} search={deleting} onDone={() => setDeleting(null)} /> : null}
    </div>
  );
}

/** Delete Collection: asked first; the documents stay, and the filter stays as an unsaved one. */
export function DeleteCollectionDialog({ ws, search, onDone }: { ws: Workspace; search: SavedSearch; onDone: () => void }) {
  return (
    <ConfirmDialog
      title={`Delete “${search.name}”?`}
      message={<p>The collection goes; its documents stay where they are.</p>}
      confirm="Delete"
      onCancel={onDone}
      onConfirm={() => {
        onDone();
        if (!ws.live) return;
        planEdit(ws.live, (e) => e.removeSavedSearch(search.id));
        announce(`Collection “${search.name}” deleted`);
        const f = ws.filter;
        if (f?.state.filter?.id === search.id) f.set({ ...f.state, filter: { ...f.state.filter, id: makeID() } });
      }}
    />
  );
}

// MARK: ⊕

/** The Add Filter menu: quick filters, collections, then each facet (its editor opens to choose the values). */
function addEntries(ws: Workspace, filter: BinderFilter, project: OpenedProject, tags: readonly { tag: string; count: number }[], edit: (e: Editing) => void): MenuEntry[] {
  const fields = project.fields();
  const has = (id: string) => fields.some((f) => f.id === id && (f.type !== 'enum' || f.options.length > 0));
  const adding = !!filter.active;
  const add = (rule: SavedSearchRule) => filter.addFacet(rule);
  const addAndEdit = (rule: SavedSearchRule, kind?: Editing['kind']) => {
    filter.addFacet(rule);
    edit({ id: rule.id, kind });
  };
  const entities = project.record.entities;
  const hasKind = (...kinds: EntityKind[]) => entities.some((e) => kinds.includes(e.kind));
  const searches = savedSearchesOf(project.record.settings);
  const node = ws.node;
  const within = node ? (node.isFolder || node.children.length > 0 ? node : ws.fullTree ? pathTo(ws.fullTree, node.id)?.at(-2) ?? null : null) : null;
  const categories = readCommentCategories(project.record);
  const own = fields.filter((f) => !STANDARD.has(f.id) && ['enum', 'boolean', 'text', 'tags'].includes(f.type));

  const entries: MenuEntry[] = [
    { kind: 'heading', label: 'Quick Filters' },
    ...quickFilters(!!beatSheetOf(project.record.settings)).map((q): MenuEntry => ({ label: q.value, onSelect: () => filter.filterBy(q.rules, adding) })),
  ];
  if (searches.length) {
    entries.push({ kind: 'separator' }, { kind: 'heading', label: 'Collections' });
    for (const s of searches) entries.push({ kind: 'check', label: s.name, checked: filter.saved?.id === s.id, onSelect: () => filter.load(s) });
  }
  entries.push({ kind: 'separator' }, { kind: 'heading', label: 'Workflow' });
  for (const id of ['status', 'label']) {
    const f = fields.find((x) => x.id === id);
    if (f && has(id)) entries.push({ label: `${f.name}…`, onSelect: () => addAndEdit(newRule({ kind: 'field', field: id })) });
  }
  if (tags.length) entries.push({ label: 'Tag…', onSelect: () => addAndEdit(newRule({ kind: 'field', field: 'tags' })) });
  if (has('section-type')) entries.push({ label: 'Section Type…', onSelect: () => addAndEdit(newRule({ kind: 'field', field: 'section-type' })) });
  entries.push(
    { label: 'Kind…', onSelect: () => addAndEdit(newRule({ kind: 'kind' })) },
    { label: 'In the Compile', onSelect: () => add(newRule({ kind: 'included-in-compile' })) },
    { label: 'Locked', onSelect: () => add(newRule({ kind: 'locked' })) },
  );
  if (entities.length) {
    entries.push({ kind: 'separator' }, { kind: 'heading', label: 'People, Places and Things' });
    if (hasKind('character')) {
      entries.push(
        { label: 'Character…', onSelect: () => addAndEdit(newRule({ kind: 'entity' }), 'character') },
        { label: 'POV…', onSelect: () => addAndEdit(newRule({ kind: 'entity', level: 'pov' }), 'character') },
      );
    }
    if (hasKind('place')) {
      if (fields.some((f) => f.id === 'setting')) entries.push({ label: 'Setting…', onSelect: () => addAndEdit(newRule({ kind: 'field', field: 'setting' })) });
      entries.push({ label: 'Place Mentioned or Present…', onSelect: () => addAndEdit(newRule({ kind: 'entity' }), 'place') });
    }
    if (hasKind('object', 'motif')) entries.push({ label: 'Object or Motif…', onSelect: () => addAndEdit(newRule({ kind: 'entity' }), 'object-or-motif') });
  }
  entries.push({ kind: 'separator' }, { kind: 'heading', label: 'Story and Structure' });
  if (beatSheetOf(project.record.settings) && has('beat')) entries.push({ label: 'Beat…', onSelect: () => addAndEdit(newRule({ kind: 'field', field: 'beat' })) });
  if (within) entries.push({ label: `Within “${within.title || 'Untitled'}”`, onSelect: () => filter.focus(within.id) });
  entries.push(
    { kind: 'separator' }, { kind: 'heading', label: 'Progress' },
    { label: 'Unwritten', onSelect: () => filter.filterBy([newRule({ kind: 'words', op: 'lt', number: 50 }), newRule({ kind: 'kind', value: 'document' })], true) },
    { label: 'Words…', onSelect: () => addAndEdit(newRule({ kind: 'words', op: 'lt', number: 1000 })) },
    { label: 'Word Target…', onSelect: () => addAndEdit(newRule({ kind: 'target', value: 'under' })) },
    { label: 'Changed…', onSelect: () => addAndEdit(newRule({ kind: 'modified-within', days: 7 })) },
    { kind: 'separator' }, { kind: 'heading', label: 'Feedback' },
    { label: 'Open Comments', onSelect: () => add(newRule({ kind: 'comments' })) },
    ...categories.map((c): MenuEntry => ({ label: `Comments: ${c.name}`, color: cssColor(INK_RGB[c.color] ?? null), onSelect: () => add(newRule({ kind: 'comments', category: c.id })) })),
    ...(categories.length ? [{ label: 'Comments Without a Category', onSelect: () => add(newRule({ kind: 'comments', category: 'none' })) }] : []),
    { label: 'Comments From Agents', onSelect: () => add(newRule({ kind: 'comments', author: 'agent' })) },
    { label: 'Open Suggestions', onSelect: () => add(newRule({ kind: 'suggestions' })) },
    { kind: 'separator' }, { kind: 'heading', label: 'Content' },
    { label: 'Title Contains…', onSelect: () => addAndEdit(newRule({ kind: 'title' })) },
    { label: 'Synopsis Contains…', onSelect: () => addAndEdit(newRule({ kind: 'synopsis' })) },
    { label: 'No Synopsis', onSelect: () => add(newRule({ kind: 'synopsis', op: 'empty' })) },
    { label: 'Notes Contain…', onSelect: () => addAndEdit(newRule({ kind: 'notes' })) },
  );
  if (own.length) {
    entries.push({ kind: 'separator' }, { kind: 'heading', label: 'Fields' });
    for (const f of own) {
      if (f.type === 'enum' || f.type === 'tags') entries.push({ label: `${f.name}…`, onSelect: () => addAndEdit(newRule({ kind: 'field', field: f.id })) });
      else if (f.type === 'boolean') entries.push({ label: `${f.name}…`, onSelect: () => addAndEdit(newRule({ kind: 'field', field: f.id, value: 'true' })) });
      else entries.push({ label: `${f.name} Contains…`, onSelect: () => addAndEdit(newRule({ kind: 'field', field: f.id, op: 'contains' })) });
    }
  }
  return entries;
}

const STANDARD = new Set(['status', 'label', 'pov', 'word-target', 'section-type', 'beat', 'post-number', 'publish-date', 'tags', 'story-time', 'story-duration', 'setting']);

/** Comment categories' inks as the menu's dots ("r g b", the Paper text inks). */
const INK_RGB: Record<string, string> = {
  red: '0.698 0.227 0.180', orange: '0.722 0.369 0.090', yellow: '0.561 0.447 0.035', green: '0.243 0.490 0.227',
  blue: '0.184 0.369 0.620', purple: '0.431 0.290 0.620', pink: '0.682 0.271 0.467', grey: '0.478 0.451 0.420',
};

// MARK: The matches as a list

/** The filter's matches in reading order (read only), each with the words found around the typed text. */
export function FilterList({ tree, selected, hrefFor, onChoose }: {
  tree: TreeNode[];
  selected: string | null;
  hrefFor: (n: TreeNode) => string;
  onChoose: (n: TreeNode) => void;
}) {
  const ws = useWorkspace();
  const view = ws?.filter?.view;
  if (!ws || !view) return null;
  const nodes = orderedMatches(tree, view.result);
  if (!nodes.length) return <p className="empty-note filter-empty">Nothing matches.</p>;
  return (
    <ul className="filter-list" aria-label="Matches">
      {nodes.map((n) => {
        const snip = n.fragmentId ? ws.filter!.snippet(n.fragmentId) : null;
        return (
          <li key={n.id} className={`filter-list-row${n.id === selected ? ' selected' : ''}${view.kept === n.id ? ' context' : ''}`} data-node-id={n.id}>
            <a
              href={hrefFor(n)}
              aria-current={n.id === selected ? 'page' : undefined}
              onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                onChoose(n);
              }}
            >
              <span className="filter-list-title">{n.title || 'Untitled'}</span>
              {snip ? <SnippetLine snippet={snip} /> : null}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

/** The words found, in their sentence. */
export function SnippetLine({ snippet }: { snippet: Snippet }) {
  return (
    <span className="filter-snippet">
      {snippet.before}
      <strong>{snippet.hit}</strong>
      {snippet.after}
    </span>
  );
}

// MARK: Filter By, Focus, and Write's banner

/** A row's or card's Filter By entries (its POV, status, label, setting, beat, tags, the folder it's in) and Focus on a
 *  folder. With a filter on, the choices add to it. */
export function filterMenuEntries(ws: Workspace, nodeId: string): MenuEntry[] {
  const filter = ws.filter;
  const project = ws.project as OpenedProject;
  if (!filter || !project.record) return [];
  const tree = ws.fullTree ?? ws.tree;
  const choices = filterChoices(project, tree, nodeId);
  const adding = !!filter.active;
  const entries: MenuEntry[] = [];
  if (choices.length) {
    entries.push({ kind: 'separator' }, { kind: 'heading', label: adding ? 'Add to Filter' : 'Filter By' });
    for (const c of choices) entries.push({ label: `${c.facet}: ${c.value}`, onSelect: () => filter.filterBy(c.rules, adding) });
  }
  const node = pathTo(tree, nodeId)?.at(-1);
  if (node && isFocusable(node) && focusNodeId(filter.state) !== nodeId) {
    if (!choices.length) entries.push({ kind: 'separator' });
    entries.push({ label: `Focus on “${node.title || 'Untitled'}”`, onSelect: () => filter.focus(nodeId) });
  }
  return entries;
}

/** Above Write's page while the filter leaves some of what's open out: how many show, and Show All. */
export function FilterBanner() {
  const ws = useWorkspace();
  const filter = ws?.filter;
  if (!ws || !filter?.view || !filter.active) return null;
  const counts = filteredCounts(ws.fullTree ?? ws.tree, filter.view.result, ws.node?.id ?? null);
  if (!counts) return null;
  const project = ws.project as OpenedProject;
  const nodeTitle = (id: string) => (ws.fullTree ? pathTo(ws.fullTree, id)?.at(-1)?.title || 'Untitled' : null);
  const facets = filter.active.rules.map((r) => (r.id === TYPED_RULE_ID ? `“${r.values[0]}”` : facetLabel(r, project, nodeTitle))).join(', ');
  return (
    <div className="filter-banner" role="status" data-filter-banner>
      <span>
        Showing {counts.shown} of {counts.total} · {facets}
      </span>
      <button type="button" className="link-button" onClick={() => filter.clear()}>
        Show All
      </button>
    </div>
  );
}

/**
 * The filter in use, as a card (the design's): a collection's colour and icon, or the accent and "Filter" for chips not
 * saved; "Edited" when the chips no longer say what the collection says; how much matches, with a bar; the rules as
 * chips joined by "and" / "or" under a caption (all or any, and where a collection looks), folded away on request.
 * Pencil = Edit Collection… (Save as Collection… unsaved), chevron folds, × clears. Its context menu has the rest.
 */
function FilterCard({ ws, filter, project, noun, writable, editing, label, parts, onChip, onRemoveChip, onEdit, onSaveAsNew, onUpdate, onDelete }: {
  ws: Workspace;
  filter: BinderFilter;
  project: OpenedProject;
  noun: DocumentNoun;
  writable: boolean;
  editing: string | null;
  label: (rule: SavedSearchRule) => string;
  parts: (rule: SavedSearchRule) => ReturnType<typeof facetParts>;
  onChip: (id: string) => void;
  onRemoveChip: (id: string) => void;
  onEdit: () => void;
  onSaveAsNew: () => void;
  onUpdate: () => void;
  onDelete: () => void;
}) {
  const [folded, setFolded] = useSharedPreference<boolean>(PREF.collectionSummaryFolded, false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const { state, saved, edited, view } = filter;
  const chips = state.filter?.rules ?? [];
  const ink: Ink = saved ? (collectionInk(saved) as Ink) : 'accent';
  const name = saved?.name ?? 'Filter';
  const unsupported = !!saved && (isUnsupported(saved) || saved.rules.some((r) => r.kind === 'unsupported'));
  const rules = chips.filter((r) => !isUnfilled(r)).length + (state.text.trim() ? 1 : 0);
  const matchAll = state.filter?.matchAll ?? true;
  const caption = filterCaption(rules, matchAll, saved);
  const join = matchAll ? 'and' : 'or';
  const entries = (): MenuEntry[] => [
    { kind: 'radio', label: 'Show as List', checked: state.list, onSelect: () => filter.set({ ...state, list: true }) },
    { kind: 'radio', label: 'Show as Shelf', checked: !state.list, onSelect: () => filter.set({ ...state, list: false }) },
    { kind: 'separator' },
    ...(saved
      ? [
          ...(edited
            ? [
                { label: `Update “${saved.name}”`, disabled: !writable, onSelect: onUpdate },
                { label: 'Revert', onSelect: () => filter.load(saved) },
              ]
            : []),
          { label: 'Save as New Collection…', disabled: !writable || !filter.active, onSelect: onSaveAsNew },
          { label: 'Edit Collection…', disabled: !writable, onSelect: onEdit },
          { label: 'Delete Collection…', danger: true, disabled: !writable, onSelect: onDelete },
        ]
      : [{ label: 'Save as Collection…', disabled: !writable || !filter.active, onSelect: onEdit }]),
    { kind: 'separator' },
    { label: 'Clear Filter', onSelect: () => filter.clear() },
  ];
  return (
    <section
      className={`filter-card tint-${ink}`}
      aria-label={saved ? `Collection ${saved.name}` : 'Filter'}
      data-collection-summary={saved ? saved.id : undefined}
      data-ink={ink}
      onContextMenu={(e) => {
        if ((e.target as HTMLElement).closest('input, .filter-chip')) return;
        e.preventDefault();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
    >
      <div className="filter-card-head">
        <Tile icon={saved ? collectionIcon(saved) : 'filter'} ink={ink} size={30} />
        <div className="filter-card-titles">
          <p className="filter-card-name">
            <span className="filter-collection">{name}</span>
            {edited ? <span className="filter-edited"> Edited</span> : null}
          </p>
          {view ? (
            <p className="filter-count-line" role="status" data-documents={view.result.documents} data-total={view.result.total}>
              {countLine(view.result, noun)}
            </p>
          ) : null}
        </div>
        <div className="filter-card-buttons">
          <button type="button" className="card-button" aria-label={saved ? 'Edit Collection…' : 'Save as Collection…'} title={saved ? 'Edit Collection…' : 'Save as Collection…'} disabled={!writable || (!saved && !filter.active)} onClick={onEdit}>
            <Icon name="pencil" />
          </button>
          <button type="button" className={`card-button fold${folded ? '' : ' open'}`} aria-label="Rules" title={folded ? 'Show the rules' : 'Hide the rules'} aria-expanded={!folded} onClick={() => setFolded(!folded)}>
            <Icon name="chevron" />
          </button>
          <button type="button" className="card-button" aria-label="Clear filter" title="Clear the filter" onClick={() => filter.clear()}>
            <Icon name="close" />
          </button>
        </div>
      </div>
      <div className="filter-card-bar" aria-hidden="true">
        <span ref={(el) => el?.style.setProperty('--fraction', String(view && view.result.total ? view.result.documents / view.result.total : saved ? 1 : 0))} />
      </div>
      {folded ? null : (
        <>
          <div className="filter-card-caption">
            <span>{caption}</span>
            <button type="button" className="card-button small" aria-pressed={state.list} aria-label="Show as a list" title={state.list ? 'Show as the shelf' : 'Show as a list'} onClick={() => filter.set({ ...state, list: !state.list })}>
              <Icon name="rows" size={13} />
            </button>
          </div>
          {unsupported ? (
            <p className="filter-summary-line">This collection was made in a newer version of Five Rooms.</p>
          ) : saved && chips.length === 0 && !state.text.trim() ? (
            <p className="filter-summary-line">
              {saved.scope === 'manuscript' ? 'Every document in the manuscript.' : saved.scope === 'research' ? 'Every document in Research & Notes.' : 'Every document in the project.'}
            </p>
          ) : null}
          {chips.length ? (
            <ul className="filter-chips" aria-label={!matchAll && chips.length > 1 ? 'Filters, any of these' : 'Filters'}>
              {chips.map((rule, i) => {
                const style = facetStyle(rule, project);
                const p = parts(rule);
                return [
                  i > 0 ? <li key={`${rule.id}-join`} className="filter-chip-join" aria-hidden="true">{join}</li> : null,
                  <li key={rule.id} className={`filter-chip${rule.negate ? ' negated' : ''}${editing === rule.id ? ' editing' : ''}`} data-rule-kind={rule.kind}>
                    <button
                      type="button"
                      className="filter-chip-name"
                      aria-label={label(rule)}
                      aria-expanded={editing === rule.id}
                      aria-controls={editing === rule.id ? 'facet-editor' : undefined}
                      title={label(rule)}
                      onClick={() => onChip(rule.id)}
                    >
                      <span className={`filter-chip-icon tint-${style.ink}`}><Icon name={style.icon} size={12} /></span>
                      {p.lead ? <span className="filter-chip-lead">{p.lead}</span> : null}
                      {p.dot ? <Dot color={p.dot} className="filter-chip-dot" /> : null}
                      <span className="filter-chip-value">{p.value}</span>
                    </button>
                    <button type="button" className="filter-chip-remove" aria-label={`Remove filter ${label(rule)}`} onClick={() => onRemoveChip(rule.id)}>
                      <Icon name="close" size={10} />
                    </button>
                  </li>,
                ];
              })}
            </ul>
          ) : null}
        </>
      )}
      {saved && edited ? (
        <p className="filter-card-edited">
          Edited ·{' '}
          <button type="button" className="link-button" disabled={!writable} onClick={onUpdate}>Update</button>
          {' '}·{' '}
          <button type="button" className="link-button" onClick={() => filter.load(saved)}>Revert</button>
        </p>
      ) : null}
      {menu ? <MenuPopup label="Filter" at={menu} entries={entries()} onClose={() => setMenu(null)} /> : null}
    </section>
  );
}

/** The contents' foot while the filter leaves documents out: how many, hidden or shown faded, and the switch between
 *  them (per browser: `ws:binderDims`, the Mac's `binderDimsFilteredOut`). */
export function FilterFoot() {
  const ws = useWorkspace();
  const [dims, setDims] = useSharedPreference<boolean>(PREF.binderDims, false);
  const view = ws?.filter?.view;
  if (!ws || !view || ws.filter?.state.list) return null;
  const noun = arrangementNoun(ws.project.type, ws.arrangement.role);
  const line = leftOutLine(view, noun, dims);
  if (!line) return null;
  return (
    <div className="filter-foot" data-filter-foot={dims ? 'dim' : 'hide'}>
      <span role="status">{line}</span>
      <button type="button" className="link-button" onClick={() => setDims(!dims)}>
        {dims ? 'Hide them' : 'Show as context'}
      </button>
    </div>
  );
}
